// Concentration + contribution helpers for the portfolio monitoring screens.
// Pure: group holdings across accounts by security, rank by exposure and by
// unrealised gain/loss. Money is in DOLLARS (matches the PortfolioSnapshot).

export interface ExposureInput {
  code: string;
  name?: string | null;
  assetClass?: string | null;
  currentValue: number;       // dollars
  costBase?: number;          // dollars
  unrealisedGainLoss?: number; // dollars
}

export interface ExposureRow {
  code: string;
  name: string | null;
  assetClass: string | null;
  value: number;              // grouped dollars
  exposurePct: number;        // value / portfolioValue, 0..1
  gainLoss: number;           // grouped dollars
  returnPct: number;          // gainLoss / costBase, 0..1 (0 if no cost base)
}

/** Group flat holdings (possibly the same security across accounts) by code. */
export function groupHoldings(holdings: ExposureInput[], portfolioValue: number): ExposureRow[] {
  const byCode = new Map<string, ExposureRow & { cost: number }>();
  for (const h of holdings) {
    const cur = byCode.get(h.code) ?? {
      code: h.code, name: h.name ?? null, assetClass: h.assetClass ?? null,
      value: 0, exposurePct: 0, gainLoss: 0, returnPct: 0, cost: 0,
    };
    cur.value += h.currentValue;
    cur.gainLoss += h.unrealisedGainLoss ?? 0;
    cur.cost += h.costBase ?? 0;
    if (!cur.name && h.name) cur.name = h.name;
    byCode.set(h.code, cur);
  }
  const rows = [...byCode.values()].map((r) => ({
    code: r.code,
    name: r.name,
    assetClass: r.assetClass,
    value: round2(r.value),
    exposurePct: portfolioValue > 0 ? round4(r.value / portfolioValue) : 0,
    gainLoss: round2(r.gainLoss),
    returnPct: r.cost > 0 ? round4(r.gainLoss / r.cost) : 0,
  }));
  return rows;
}

/** Top N positions by current value. */
export function topExposures(holdings: ExposureInput[], portfolioValue: number, n = 5): ExposureRow[] {
  return groupHoldings(holdings, portfolioValue).sort((a, b) => b.value - a.value).slice(0, n);
}

/** Top N losing positions (most negative unrealised gain/loss). Only negatives. */
export function largestDetractors(holdings: ExposureInput[], portfolioValue: number, n = 5): ExposureRow[] {
  return groupHoldings(holdings, portfolioValue)
    .filter((r) => r.gainLoss < 0)
    .sort((a, b) => a.gainLoss - b.gainLoss)
    .slice(0, n);
}

function round2(n: number) { return Math.round(n * 100) / 100; }
function round4(n: number) { return Math.round(n * 10000) / 10000; }
